import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { buildRedirectUrl } from '@/lib/session';
import { IdleWarningModal } from './IdleWarningModal';

/**
 * Owns the idle-logout timer and the ONE place the app navigates on sign-out.
 *
 * Mounted once at the app root (inside BrowserRouter, beside CookieBanner) rather
 * than in Layout, for three reasons:
 *
 *  - /disclaimer and /scan/:token are authenticated but render OUTSIDE Layout, so
 *    a Layout-based watcher would leave them uncovered. /disclaimer is the one
 *    that matters: it's a full-screen page users genuinely sit and read, and with
 *    Layout unmounted its 5s notification poll isn't running either, so there
 *    would be neither a client timer nor a server 401 to catch the expiry.
 *  - It never remounts on navigation, so the timer doesn't reset when a user
 *    crosses into or out of /disclaimer.
 *  - The redirect effect below needs to survive the unmount of everything else.
 *
 * The redirect deliberately happens HERE, in an effect, rather than inside
 * endSession(). Clearing the token makes RequireAuth render <Navigate>, unmounting
 * Layout and with it all four `beforeunload` guards and useUnsavedChangesGuard's
 * pushState patch. An effect runs by definition after that commit, so the
 * document navigation can't trigger the browser's "Leave site?" prompt. See
 * lib/session.ts for the full reasoning.
 */
export function SessionWatcher() {
  const token = useAuthStore((s) => s.token);
  const endReason = useAuthStore((s) => s.endReason);
  const location = useLocation();

  useEffect(() => {
    if (!endReason) return;

    // Replace rather than push, so Back can't return to the signed-out page. A
    // full document load is intentional: it's the only real guarantee that
    // nothing PHI-bearing survives in memory (query cache, closures, detached
    // DOM, Sentry breadcrumbs).
    window.location.replace(buildRedirectUrl(endReason));
  }, [endReason]);

  // The patient portal is a separate auth surface with its own store, token and
  // policy; a staff token left in the same browser must not drive a timer over
  // it. Unmounting the inner component (rather than ignoring its output) is what
  // actually stops the listeners and the interval.
  const isPortal = location.pathname.startsWith('/portal');
  if (!token || isPortal) return null;

  return <IdleWatcher />;
}

/** Split out so its hooks only run while there is a staff session to watch. */
function IdleWatcher() {
  const { phase, secondsRemaining, staySignedIn, signOutNow } = useIdleLogout();

  return (
    <IdleWarningModal
      isOpen={phase === 'warn'}
      secondsRemaining={secondsRemaining}
      onStaySignedIn={staySignedIn}
      onSignOutNow={signOutNow}
    />
  );
}
